import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Scale,
  Sparkles,
  Plus,
  X,
  Search,
  Loader2,
  AlertCircle,
  ShieldCheck,
  Info,
} from "lucide-react";
import {
  useLegalFramework,
  useSuggestLegalFramework,
  useAddLegalFrameworkNorm,
  useRemoveLegalFrameworkNorm,
  useLegalCatalog,
  type LegalFrameworkItem,
  type LegalReference,
} from "@/hooks/use-legal";

type FrameworkSource = LegalFrameworkItem["source"];

const sourceChip: Record<FrameworkSource, { label: string; className: string }> = {
  base: { label: "Núcleo", className: "bg-gray-100 text-gray-600" },
  ia: { label: "Sugerida IA", className: "bg-purple-100 text-purple-700" },
  manual: { label: "Manual", className: "bg-blue-100 text-blue-700" },
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    vigente: "bg-emerald-50 text-emerald-700 border-emerald-200",
    derogada: "bg-red-50 text-red-700 border-red-200",
    modificada: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium ${
        map[status] ?? "bg-gray-50 text-gray-600 border-gray-200"
      }`}
    >
      {status}
    </span>
  );
}

function NormRow({
  reference,
  source,
  onRemove,
  removing,
}: {
  reference: LegalReference;
  source: FrameworkSource;
  onRemove?: () => void;
  removing?: boolean;
}) {
  const chip = sourceChip[source];
  return (
    <li className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50">
      <Scale className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-gray-800">{reference.short_title}</span>
          <span className="text-sm text-gray-500">{reference.number}</span>
          <StatusBadge status={reference.status} />
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${chip.className}`}>
            {chip.label}
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm text-gray-600" title={reference.title}>
          {reference.title}
        </p>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={removing}
          className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
          aria-label={`Quitar ${reference.short_title} del marco`}
          title={
            source === "base"
              ? "Quitar de esta licitación (reversible)"
              : "Quitar del marco"
          }
        >
          {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
        </button>
      )}
    </li>
  );
}

export default function LicitacionMarcoLegalPage() {
  const { id } = useParams<{ id: string }>();
  const licitacionId = id ?? "";

  const { data, isLoading, isError } = useLegalFramework(licitacionId);
  const suggest = useSuggestLegalFramework(licitacionId);
  const addNorm = useAddLegalFrameworkNorm(licitacionId);
  const removeNorm = useRemoveLegalFrameworkNorm(licitacionId);
  const { data: catalog } = useLegalCatalog();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [removingSlug, setRemovingSlug] = useState<string | null>(null);

  const items = data?.items ?? [];
  const baseItems = items.filter((it) => it.source === "base");
  const specificItems = items.filter((it) => it.source !== "base");

  const frameworkIds = useMemo(
    () => new Set(items.map((it) => it.reference.id)),
    [items]
  );

  // Catálogo disponible para añadir (excluye lo que ya está en el marco).
  const availableNorms = useMemo(() => {
    const laws = catalog?.laws ?? [];
    const q = query.trim().toLowerCase();
    return laws
      .filter((law) => !frameworkIds.has(law.id))
      .filter(
        (law) =>
          !q ||
          law.short_title.toLowerCase().includes(q) ||
          law.title.toLowerCase().includes(q) ||
          law.number.toLowerCase().includes(q) ||
          law.category.toLowerCase().includes(q)
      );
  }, [catalog, frameworkIds, query]);

  function handleRemove(slug: string) {
    setRemovingSlug(slug);
    removeNorm.mutate(slug, { onSettled: () => setRemovingSlug(null) });
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Encabezado explicativo + acciones */}
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-800">
            <Scale className="h-5 w-5 text-blue-600" />
            Marco legal de la licitación
          </h1>
          <p className="mt-1 max-w-xl text-sm text-gray-600">
            Las normas que aplican específicamente a esta licitación. El núcleo común
            (LCSP, RGPD…) se incluye siempre; añade las sectoriales a mano o deja que la
            IA las proponga a partir del pliego.
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <button
            type="button"
            onClick={() => suggest.mutate()}
            disabled={suggest.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-60"
          >
            {suggest.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Sugerir desde el pliego
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            Añadir norma
          </button>
        </div>
      </div>

      {/* Picker del catálogo */}
      {pickerOpen && (
        <div className="mb-5 rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar en el catálogo (ley, número, categoría)…"
              className="flex-1 text-sm focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="rounded p-1 text-gray-400 hover:bg-gray-100"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <ul className="max-h-72 divide-y divide-gray-50 overflow-y-auto">
            {availableNorms.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-gray-500">
                No hay más normas en el catálogo que añadir.
              </li>
            )}
            {availableNorms.map((law) => (
              <li
                key={law.id}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-800">{law.short_title}</span>
                    <span className="text-xs text-gray-500">{law.number}</span>
                    <StatusBadge status={law.status} />
                  </div>
                  <p className="truncate text-xs text-gray-500" title={law.title}>
                    {law.title}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => addNorm.mutate(law.id)}
                  disabled={addNorm.isPending}
                  className="flex-shrink-0 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                >
                  Añadir
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Estados */}
      {isLoading && (
        <div className="flex items-center gap-2 py-10 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando marco legal…
        </div>
      )}
      {isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> No se pudo cargar el marco legal.
        </div>
      )}

      {!isLoading && !isError && (
        <div className="space-y-5">
          {/* Normas específicas (IA + manual) */}
          <section className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-700">
                Normas específicas de esta licitación
              </h2>
              <p className="text-xs text-gray-500">
                Sectoriales sugeridas por la IA o añadidas a mano.
              </p>
            </div>
            {specificItems.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Info className="mx-auto mb-2 h-5 w-5 text-gray-300" />
                <p className="text-sm text-gray-500">
                  Aún no hay normas específicas. Pulsa{" "}
                  <span className="font-medium text-purple-600">Sugerir desde el pliego</span>{" "}
                  o <span className="font-medium text-gray-700">Añadir norma</span>.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {specificItems.map((it) => (
                  <NormRow
                    key={it.reference.id}
                    reference={it.reference}
                    source={it.source}
                    onRemove={() => handleRemove(it.reference.id)}
                    removing={removingSlug === it.reference.id}
                  />
                ))}
              </ul>
            )}
          </section>

          {/* Núcleo común (base) */}
          <section className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
              <ShieldCheck className="h-4 w-4 text-gray-400" />
              <div>
                <h2 className="text-sm font-semibold text-gray-700">Núcleo común</h2>
                <p className="text-xs text-gray-500">
                  Aplica a toda licitación pública. Si alguna no aplica aquí,
                  puedes quitarla (y volver a añadirla cuando quieras).
                </p>
              </div>
            </div>
            <ul className="divide-y divide-gray-100">
              {baseItems.map((it) => (
                <NormRow
                  key={it.reference.id}
                  reference={it.reference}
                  source={it.source}
                  onRemove={() => handleRemove(it.reference.id)}
                  removing={removingSlug === it.reference.id}
                />
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}
